// Industry page content — one entry per /industries/:slug route.

export const INDUSTRIES = [
  {
    slug: "hvac",
    icon: "Flame",
    shortName: "HVAC",
    title: "HVAC CRM Software",
    metaTitle: "HVAC CRM & Service Software | ServiceGuru Pro",
    metaDescription: "ServiceGuru Pro is HVAC CRM and service software built for dispatching, scheduling, estimates, invoicing, and AI-assisted diagnostics — purpose-built for HVAC companies.",
    primaryKeyword: "HVAC CRM software",
    intro: "HVAC companies juggle emergency calls, seasonal maintenance plans, and technicians who need answers fast in front of the equipment. ServiceGuru Pro brings dispatching, scheduling, estimates, and AI diagnostics into one platform built around that reality.",
    challenges: [
      "Emergency no-cool or no-heat calls need the closest available technician dispatched immediately.",
      "Maintenance agreement customers expect reliable recurring scheduling and consistent member pricing.",
      "Technicians need fast, accurate answers on unfamiliar error codes without calling a senior tech.",
    ],
    benefits: [
      { title: "Fast emergency dispatch", desc: "See technician location live and assign the nearest available tech to an emergency no-heat or no-cool call." },
      { title: "Maintenance plan memberships", desc: "Sell and auto-renew seasonal tune-up memberships with automatic member pricing on every invoice." },
      { title: "AI diagnostic assistant", desc: "Technicians get instant, manual-grounded guidance on error codes and symptoms right at the unit." },
      { title: "Estimates and invoicing built in", desc: "Quote a repair on-site, get a signature, and invoice the moment the job is done." },
    ],
    faqs: [
      { question: "Does ServiceGuru Pro work for HVAC companies?", answer: "Yes, ServiceGuru Pro is built for HVAC dispatching, scheduling, maintenance plans, estimates, invoicing, and AI-assisted diagnostics." },
      { question: "Can HVAC technicians get AI help diagnosing error codes?", answer: "Yes, the AI diagnostic assistant is trained on your uploaded equipment manuals to help technicians troubleshoot error codes and symptoms in the field." },
      { question: "Does ServiceGuru Pro support HVAC maintenance agreements?", answer: "Yes, the memberships feature supports recurring maintenance plans with automatic renewal and member pricing." },
    ],
    relatedFeatures: ["dispatching", "ai-diagnostic-assistant", "memberships", "estimates"],
    relatedIndustries: ["plumbing", "electrical"],
  },
  {
    slug: "plumbing",
    icon: "Droplets",
    shortName: "plumbing",
    title: "Plumbing Business Software",
    metaTitle: "Plumbing CRM & Business Software | ServiceGuru Pro",
    metaDescription: "Plumbing business software with dispatching, estimates, invoicing, and an AI receptionist for after-hours emergency calls — built for plumbing companies by ServiceGuru Pro.",
    primaryKeyword: "plumbing business software",
    intro: "Plumbing emergencies don't wait for business hours. ServiceGuru Pro combines fast dispatching, on-site estimates, and an AI receptionist so no after-hours call ever goes unanswered.",
    challenges: [
      "Burst pipes and after-hours emergencies need to be answered and dispatched immediately, any time of day.",
      "Estimating parts and labor accurately on the spot, without a laptop, slows technicians down.",
      "Missed calls during a busy job often mean a lost customer to a competitor.",
    ],
    benefits: [
      { title: "AI receptionist for after-hours calls", desc: "Never miss an emergency call again — the AI receptionist answers and books appointments 24/7." },
      { title: "On-site estimates", desc: "Build a parts-and-labor estimate from the pricebook right from a phone in the customer's driveway." },
      { title: "GPS-aware emergency dispatch", desc: "Route the closest available technician to an emergency call using live GPS location." },
      { title: "Online payments", desc: "Collect payment on the spot with a card reader or a pay link, no chasing checks." },
    ],
    faqs: [
      { question: "Does ServiceGuru Pro include dispatching?", answer: "Yes, a full drag-and-drop dispatch board with live technician GPS location is included." },
      { question: "Can plumbing companies use the AI receptionist for after-hours calls?", answer: "Yes, the AI receptionist can answer and book calls around the clock, including nights and weekends." },
    ],
    relatedFeatures: ["ai-receptionist", "dispatching", "estimates", "online-payments"],
    relatedIndustries: ["hvac", "electrical"],
  },
  {
    slug: "electrical",
    icon: "Zap",
    shortName: "electrical",
    title: "Electrical Contractor CRM",
    metaTitle: "Electrical Contractor CRM Software | ServiceGuru Pro",
    metaDescription: "Electrical contractor CRM with job documentation, estimates, invoicing, and payroll built for electrical companies managing crews and code-compliant jobs.",
    primaryKeyword: "electrical contractor CRM",
    intro: "From panel upgrades to service calls, electrical contractors need clean job documentation, accurate estimates, and payroll that handles commission and piece-rate work.",
    challenges: [
      "Every job needs clear notes and photos for code-compliance and warranty documentation.",
      "Estimating larger projects like panel upgrades or rewires takes time and needs to stay consistent across estimators.",
      "Paying a mixed crew of hourly apprentices and commissioned journeymen gets complicated fast.",
    ],
    benefits: [
      { title: "Job notes and photo documentation", desc: "Attach notes and photos to every job for compliance records and warranty history." },
      { title: "Consistent estimating", desc: "Quote from a shared pricebook so every estimator prices jobs the same way." },
      { title: "Flexible payroll", desc: "Pay apprentices hourly and journeymen on commission or piece rate from the same payroll run." },
      { title: "Missed-call protection", desc: "The AI receptionist catches calls your office can't get to during a busy install." },
    ],
    faqs: [
      { question: "Can ServiceGuru Pro handle mixed hourly and commission pay for electricians?", answer: "Yes, payroll supports hourly, commission, and piece-rate pay models per technician in the same pay run." },
    ],
    relatedFeatures: ["job-management", "estimates", "payroll", "ai-receptionist"],
    relatedIndustries: ["hvac", "general-contractors"],
  },
  {
    slug: "pool-service",
    icon: "Waves",
    shortName: "pool service",
    title: "Pool Service Software",
    metaTitle: "Pool Service Software & Scheduling | ServiceGuru Pro",
    metaDescription: "Pool service software with recurring route scheduling, membership billing, and GPS tracking — built for pool service and maintenance companies.",
    primaryKeyword: "pool service software",
    intro: "Pool service runs on recurring routes and recurring revenue. ServiceGuru Pro schedules repeat visits automatically and bills maintenance memberships without manual follow-up.",
    challenges: [
      "Weekly or bi-weekly route visits need to schedule themselves, not get rebuilt every week by hand.",
      "Recurring maintenance plans need automatic, predictable billing.",
      "Owners want visibility into which routes and technicians are running behind.",
    ],
    benefits: [
      { title: "Recurring route scheduling", desc: "Set up weekly or bi-weekly maintenance visits once and let them repeat automatically." },
      { title: "Membership billing", desc: "Bill recurring maintenance plans automatically with member pricing applied to every visit." },
      { title: "Live route tracking", desc: "See exactly where every route technician is and whether the day's route is on schedule." },
      { title: "Simple mobile check-ins", desc: "Technicians log chemical readings and notes per visit from their phone." },
    ],
    faqs: [
      { question: "Can ServiceGuru Pro handle recurring pool maintenance schedules?", answer: "Yes, recurring appointments and membership billing are both built in for repeat maintenance routes." },
    ],
    relatedFeatures: ["scheduling", "memberships", "gps-fleet-tracking", "mobile-app"],
    relatedIndustries: ["landscaping", "hvac"],
  },
  {
    slug: "landscaping",
    icon: "Trees",
    shortName: "landscaping",
    title: "Landscaping Business Software",
    metaTitle: "Landscaping Business Software | ServiceGuru Pro",
    metaDescription: "Landscaping business software with crew scheduling, recurring contracts, team messaging, and a mobile app built for landscaping and lawn care companies.",
    primaryKeyword: "landscaping business software",
    intro: "Landscaping crews live on the road, not behind a desk. ServiceGuru Pro's mobile app, recurring scheduling, and team messaging keep every crew coordinated without a single spreadsheet.",
    challenges: [
      "Recurring seasonal contracts need to schedule themselves week after week.",
      "Crews without desks need a phone-friendly way to see their day and log job notes.",
      "Coordinating multiple crews across properties requires fast team communication.",
    ],
    benefits: [
      { title: "Recurring contract scheduling", desc: "Season-long lawn care or landscaping contracts schedule their recurring visits automatically." },
      { title: "Mobile-first for crews", desc: "Crew leads use the mobile app to see the day's stops and log completed work." },
      { title: "Team messaging", desc: "Coordinate multiple crews across properties with built-in group messaging." },
      { title: "GPS route tracking", desc: "See which crew is at which property and how the day's route is progressing." },
    ],
    faqs: [
      { question: "Can landscaping crews use ServiceGuru Pro without a laptop?", answer: "Yes, the mobile app covers everything a crew lead needs — schedule, job notes, and messaging — from a phone." },
    ],
    relatedFeatures: ["scheduling", "mobile-app", "team-messaging", "gps-fleet-tracking"],
    relatedIndustries: ["pool-service", "roofing"],
  },
  {
    slug: "roofing",
    icon: "Home",
    shortName: "roofing",
    title: "Roofing Company Software",
    metaTitle: "Roofing Company CRM & Estimating Software | ServiceGuru Pro",
    metaDescription: "Roofing company software with large-project estimating, deposit collection, sales pipeline tracking, and profitability reporting from ServiceGuru Pro.",
    primaryKeyword: "roofing company software",
    intro: "Roofing sales cycles are longer and the numbers are bigger. ServiceGuru Pro tracks every quoted job through a sales pipeline, collects deposits online, and reports on job profitability.",
    challenges: [
      "Big-ticket estimates need a clear pipeline so no quoted job gets forgotten during a long sales cycle.",
      "Collecting a deposit before work starts is critical on large jobs.",
      "Owners need to know which jobs and crews are actually profitable, not just busy.",
    ],
    benefits: [
      { title: "Sales pipeline for big quotes", desc: "Track every roofing estimate from first inspection to signed contract in a visual pipeline." },
      { title: "Deposit collection online", desc: "Collect a deposit the moment a customer accepts an estimate, before materials are ordered." },
      { title: "Job profitability reporting", desc: "See revenue and average ticket by job and by crew in the reports dashboard." },
      { title: "Branded, professional estimates", desc: "Send polished branded PDF estimates that make a large-ticket sale feel credible." },
    ],
    faqs: [
      { question: "Can roofing companies track long sales cycles?", answer: "Yes, the sales pipeline tracks quoted jobs through every stage from inspection to signed contract." },
      { question: "Can I collect a deposit before starting a roofing job?", answer: "Yes, deposits can be collected online the moment an estimate is accepted." },
    ],
    relatedFeatures: ["sales-pipeline", "estimates", "online-payments", "reports"],
    relatedIndustries: ["general-contractors", "landscaping"],
  },
  {
    slug: "general-contractors",
    icon: "HardHat",
    shortName: "general contracting",
    title: "General Contractor Software",
    metaTitle: "General Contractor Software | ServiceGuru Pro",
    metaDescription: "General contractor software with job management, sales pipeline, team messaging, and payroll for multi-phase projects and crews of any size.",
    primaryKeyword: "general contractor software",
    intro: "Multi-phase projects, subcontractor coordination, and mixed pay structures need a system that flexes with the job, not a rigid template. ServiceGuru Pro covers the full project lifecycle.",
    challenges: [
      "Multi-phase projects need job tracking that doesn't fall apart between phases.",
      "Coordinating your own crew and subcontractors requires fast, reliable communication.",
      "Payroll often mixes hourly crew, salaried supervisors, and commissioned sales staff.",
    ],
    benefits: [
      { title: "Full job lifecycle tracking", desc: "Track a multi-phase project from scheduled through completed with notes at every step." },
      { title: "Sales pipeline for bids", desc: "Track bid opportunities through your sales process without a separate tool." },
      { title: "Team messaging", desc: "Keep crew leads, office staff, and supervisors coordinated in one place." },
      { title: "Flexible payroll", desc: "Support hourly, salary, and commission pay for a mixed team in one payroll run." },
    ],
    faqs: [
      { question: "Can ServiceGuru Pro handle multi-phase construction projects?", answer: "Yes, jobs can be tracked through every phase with notes, photos, and status history retained end to end." },
    ],
    relatedFeatures: ["job-management", "sales-pipeline", "team-messaging", "payroll"],
    relatedIndustries: ["roofing", "electrical"],
  },
  {
    slug: "home-service",
    icon: "Building2",
    shortName: "home service",
    title: "Home Service Business Software",
    metaTitle: "Home Service Business Software | ServiceGuru Pro",
    metaDescription: "ServiceGuru Pro is home service business software combining CRM, dispatching, estimates, invoicing, payroll, and AI voice agents in one platform.",
    primaryKeyword: "home service business software",
    intro: "Whatever trade you run, home service businesses share the same core needs: a full schedule, fast quotes, reliable payment, and happy repeat customers. ServiceGuru Pro brings all of it into one platform.",
    challenges: [
      "Juggling scheduling, quoting, invoicing, and customer follow-up across separate tools wastes time every day.",
      "Missed calls and slow follow-up quietly cost home service businesses real revenue.",
      "Owners need one clear view of the business instead of a dozen spreadsheets.",
    ],
    benefits: [
      { title: "One platform, not five tools", desc: "CRM, dispatching, estimates, invoicing, payments, and payroll all live in one system." },
      { title: "AI that covers the phone", desc: "The AI receptionist and voice agents keep the phone answered and follow-ups moving automatically." },
      { title: "Works for any trade", desc: "The same platform adapts to HVAC, plumbing, electrical, landscaping, roofing, and more." },
      { title: "Clear reporting", desc: "One reports dashboard shows revenue, jobs, and technician performance at a glance." },
    ],
    faqs: [
      { question: "What is a home service CRM?", answer: "A home service CRM organizes customers, jobs, estimates, invoices, and communication for businesses like HVAC, plumbing, electrical, and landscaping companies in one system, rather than spreadsheets and separate tools." },
      { question: "Is ServiceGuru Pro only for one type of trade?", answer: "No, ServiceGuru Pro is built to work across HVAC, plumbing, electrical, pool service, landscaping, roofing, and general contracting businesses." },
    ],
    relatedFeatures: ["crm", "dispatching", "ai-receptionist", "reports"],
    relatedIndustries: ["hvac", "plumbing"],
  },
];

export const INDUSTRIES_BY_SLUG = Object.fromEntries(INDUSTRIES.map((i) => [i.slug, i]));
